/**
 * Origins (scheme, host and port) that belong to this installation.
 */
function ownOrigins(): Set<string> {
  const config = window.Munio?.config
  const origins = new Set([window.location.origin])

  let canonical: URL

  try {
    canonical = new URL(
      config?.baseurl || window.location.origin,
      window.location.href,
    )
  } catch {
    return origins
  }

  origins.add(canonical.origin)

  // `Invite::link` keeps the canonical scheme and port but swaps in the app domain,
  // and is not validated server side, so reconstruct that origin the same way here.
  if (config?.domain) {
    canonical.hostname = config.domain
    origins.add(canonical.origin)
  }

  return origins
}

/**
 * Resolve a redirect target into a URL that is safe to assign to `window.location`.
 *
 * Only http(s) URLs whose whole origin (scheme, host and port) is one of ours are
 * accepted, so `javascript://%0Aalert(1)`, another origin, a downgrade to plain http
 * and an unrelated service on another port can never be navigated to.
 * Returns `fallback` when the target is not safe.
 *
 * Kept free of imports so that importing it does not pull the rest of
 * `@/munio/utils` (dompurify, lodash, ...) into the http bootstrap graph.
 */
export function safeRedirect(url: unknown, fallback?: null): string | null
export function safeRedirect(url: unknown, fallback: string): string
export function safeRedirect(
  url: unknown,
  fallback: string | null = null,
): string | null {
  if (typeof url !== 'string' || !url.trim()) {
    return fallback
  }

  let resolved: URL

  try {
    // Resolve against the current URL rather than just the origin, so that a
    // relative target such as `?tab=account` keeps the current path.
    resolved = new URL(url.trim(), window.location.href)
  } catch {
    return fallback
  }

  if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') {
    return fallback
  }

  // Reject embedded credentials (`https://example.com@ourhost/`): harmless to
  // navigate to, but a convincing phishing display.
  if (resolved.username || resolved.password) {
    return fallback
  }

  return ownOrigins().has(resolved.origin) ? resolved.href : fallback
}

/**
 * Resolve a redirect target to a third party identity provider (e.g. EcoID).
 *
 * The target is cross-origin by design, so only the scheme is restricted: it must be
 * an absolute `https:` URL without embedded credentials. Returns null otherwise.
 */
export function safeExternalRedirect(url: unknown): string | null {
  if (typeof url !== 'string' || !url.trim()) {
    return null
  }

  let resolved: URL

  try {
    resolved = new URL(url.trim())
  } catch {
    return null
  }

  if (
    resolved.protocol !== 'https:' ||
    resolved.username ||
    resolved.password
  ) {
    return null
  }

  return resolved.href
}
